// ViewReservationForm.tsx
// React and MobX
import React, { useEffect } from "react";
import { useObserver } from "mobx-react-lite";
import SignatureCanvas from "react-signature-canvas";

// Material-UI Core and Styles
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Grid,
    Link,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import RemoveIcon from "@material-ui/icons/RemoveCircle";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

// Core
import { useRouter } from "Core/Base";
import { Loader } from "Core/Components/Loader";

// Custom Components
import { Error } from "Custom/Components/Error";
import { NavButton } from "Custom/Components/Buttons/NavButton";

// Custom SVG Components
import { ReactComponent as SquareLeftArrow } from "../../Content/play-backward-icon.svg";

// Custom Styles
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { ReservationType1ViewModel } from "Custom/ViewModels/DashboardSubViewModels/ReservationType1ViewModel";
import { ViewFormStyle } from "Custom/StylesAppSpecific/DashboardSubViews/ViewReservationFormStyling";
import { useDocumentsPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/DocumentPageStyling";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { isNullOrEmpty } from "Core/Utils/Utils";
import { currencyFormat } from "Custom/Utils/Format/currencyFormat";
import { EditableInput } from "Core/Components/EditableInput";
import { Format } from "Custom/Utils";
import { isNullOrUndefined } from "util";
import { PropertyViewModel } from "Custom/ViewModels/DashboardSubViewModels/PropertyViewModel";

interface IProps {
    propertyId?: string;
    reservationId?: string;
    onDrawerClose?: () => void;
    signatureToken?: string;
    purchaserType?: number;
    isSignatureRequest?: boolean;
}

export const ViewReservationForm: React.FC<IProps> = props => {
    const dashboardPageStyles = DashboardPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();
    const formPageStyles = ViewFormStyle();
    const mainPageStyles = MainPageStyles();
    const documentsPageStyles = useDocumentsPageStyles();

    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;

    // propertyId from router if not provided via props
    const propertyId = props.propertyId ?? (router.match.params as any).propertyId;

    const [viewModel] = React.useState(() => new ReservationType1ViewModel(propertyId));
    const [propertyViewModel] = React.useState(() => new PropertyViewModel(propertyId));

    // Accordion expanded panel (null = none)
    const [expanded, setExpanded] = React.useState<string | null>(null);
    const toggleAccordion = (panel: string) => {
        setExpanded(prev => (prev === panel ? null : panel));
    };

    // Signature canvas ref (stable across renders)
    const signatureCanvasRef = React.useRef<SignatureCanvas | null>(null);

    // Scroll hint visibility
    const [showScrollHint, setShowScrollHint] = React.useState<boolean>(true);

    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

    const handleContentScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        if (target.scrollTop > 5) {
            setShowScrollHint(false);
        }
    };

    const handleHintClick = () => {
        setShowScrollHint(false);
    };

    // Dynamic footer height to avoid content being covered
    const [footerHeight, setFooterHeight] = React.useState<number>(0);
    const footerRef = React.useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const updateHeight = () => {
            const h = footerRef.current ? footerRef.current.getBoundingClientRect().height : 0;
            setFooterHeight(h);
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    // Additional scroll detection for the hint
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            if (scrollContainer.scrollTop > 5) {
                setShowScrollHint(false);
            }
        };

        scrollContainer.addEventListener("scroll", handleScroll);
        return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }, []);

    // Auto-hide hint after 2 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowScrollHint(false);
        }, 2000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!signatureCanvasRef.current || !viewModel) return;

        const url = viewModel.isPurchaserOneLoggedIn ? viewModel.purchaserOneSignatureURL : viewModel.purchaserTwoSignatureURL;

        if (url) {
            signatureCanvasRef.current.fromDataURL(url);
        }
    }, [viewModel, viewModel.isPurchaserOneLoggedIn, viewModel.purchaserOneSignatureURL, viewModel.purchaserTwoSignatureURL, signatureCanvasRef.current]);

    // Check if user has already signed when component loads
    useEffect(() => {
        if (viewModel && viewModel.ApiStatus === ApiStatus.success && viewModel.currentUserSignatureComplete) {
            setShowAlreadySignedModal(true);
            // Don't show legal modal if user has already signed
            setShowLegalModal(false);
        }
    }, [viewModel, viewModel.ApiStatus, viewModel.currentUserSignatureComplete]);

    // Modal: Legal information must be acknowledged before showing the form
    const [showLegalModal, setShowLegalModal] = React.useState<boolean>(true);
    const [legalScrolledToBottom, setLegalScrolledToBottom] = React.useState<boolean>(false);
    const legalScrollRef = React.useRef<HTMLDivElement | null>(null);
    const legalScrollCombinedRef = React.useCallback((el: HTMLDivElement | null) => {
        if (!el) return;

        // Attach the ref so scroll handler still works
        legalScrollRef.current = el;

        const isScrollable = el.scrollHeight > el.clientHeight;

        if (!isScrollable) {
            // Content fits → allow Ok immediately
            setLegalScrolledToBottom(true);
        } else {
            // Content scrolls → force user to scroll
            setLegalScrolledToBottom(false);
        }
    }, []);

    const handleLegalScroll = () => {
        const el = legalScrollRef.current;
        if (!el) return;
        const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
        if (reachedBottom && !legalScrolledToBottom) {
            setLegalScrolledToBottom(true);
        }
    };

    const closeLegalModal = () => {
        setShowLegalModal(false);
        // const key = `reservation_legal_ack_${propertyId}`;
        // localStorage.setItem(key, "true");
    };

    // Final success modal after saving signatures
    const [showSignatureSuccess, setShowSignatureSuccess] = React.useState<boolean>(false);
    const [sendEmailCopy, setSendEmailCopy] = React.useState<boolean>(false);
    const [isSubmittingSignature, setIsSubmittingSignature] = React.useState<boolean>(false);

    // Modal for when user has already signed
    const [showAlreadySignedModal, setShowAlreadySignedModal] = React.useState<boolean>(false);

    const renderAlreadySignedModal = (): JSX.Element => {
        return (
            <Dialog open={showAlreadySignedModal} fullWidth maxWidth="xs" aria-labelledby="already-signed-title" PaperProps={{ style: { zIndex: 2100 } }}>
                <DialogContent style={{ textAlign: "center", paddingTop: 32 }}>
                    <CheckCircleOutlineIcon style={{ fontSize: 96, color: "#4CAF50", marginBottom: 8 }} />
                    <Typography variant="h5" gutterBottom style={{ font: "normal normal bold 24px / 32px Roboto", color: "#3C3C3B", opacity: 1 }}>
                        <b>Signature Already Completed</b>
                    </Typography>
                    <Typography variant="body2" paragraph>
                        You have already signed this reservation form. No further action is required.
                    </Typography>
                </DialogContent>
                <DialogActions style={{ justifyContent: "center", paddingBottom: 24 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setShowAlreadySignedModal(false);
                            // Navigate back to signatures
                            if (propertyId) {
                                viewModel.navigateToSignatures(propertyId);
                            }
                        }}
                        style={{ backgroundColor: "#556ED1", color: "#fff" }}
                    >
                        Back to Signatures
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderSignatureSuccessModal = (): JSX.Element => {
        const allComplete = viewModel.allSignaturesComplete;
        const awaitingFrom = viewModel.awaitingSignatureFrom;
        const additionalInfo = viewModel.awaitingSignatureAdditionalInfo;
        const linkExpiryInfo = viewModel.linkExpiryInfo;

        return (
            <Dialog open={showSignatureSuccess} fullWidth maxWidth="xs" aria-labelledby="signature-success-title" PaperProps={{ style: { zIndex: 2100 } }}>
                <DialogContent style={{ textAlign: "center", paddingTop: 32 }}>
                    <CheckCircleOutlineIcon style={{ fontSize: 96, color: "#556ED1", marginBottom: 8 }} />
                    <Typography variant="h5" gutterBottom style={{ font: "normal normal bold 24px / 32px Roboto", color: "#3C3C3B", opacity: 1 }}>
                        <b>Success</b>
                    </Typography>
                    {allComplete ? (
                        <Typography variant="body2" paragraph>
                            All signatures are complete! You can find a signed copy of the form in the documents section of the myHome portal.
                        </Typography>
                    ) : (
                        <Box>
                            <Typography variant="body2" paragraph>
                                {awaitingFrom}
                            </Typography>
                            {additionalInfo && (
                                <Typography variant="body2" paragraph>
                                    {additionalInfo}
                                </Typography>
                            )}
                            {linkExpiryInfo && (
                                <Typography variant="body2" paragraph style={{ fontWeight: 700 }}>
                                    {linkExpiryInfo}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Divider style={{ margin: "16px 0" }} />
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    className={formPageStyles.signatureCheckbox}
                                    color={"default"}
                                    checked={sendEmailCopy}
                                    onChange={e => setSendEmailCopy(e.target.checked)}
                                />
                            }
                            label={<Typography variant="body2">Send a copy to my email address</Typography>}
                        />
                    </Box>
                    <Divider style={{ margin: "16px 0" }} />
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (sendEmailCopy) {
                                viewModel.emailSignedReservationCopy();
                            }
                            setShowSignatureSuccess(false);
                        }}
                        fullWidth
                        style={{ backgroundColor: "#556ED1", color: "#fff" }}
                    >
                        Finish
                    </Button>
                </DialogContent>
            </Dialog>
        );
    };

    const handleSignatureEnd = () => {
        // hook for signature end; if you need to use dataURL:
        // const dataUrl = signatureCanvasRef.current?.toDataURL();
        // call viewModel or onEnd callback here
    };

    const handleSignatureClear = () => {
        signatureCanvasRef.current?.clear();
    };

    function formatList(list: string[]): string {
        if (list.length === 0) return "";
        if (list.length === 1) return list[0];
        if (list.length === 2) return list.join(" and ");
        return list.slice(0, -1).join(", ") + " and " + list[list.length - 1];
    }

    const renderBusy = () => <Loader loadingText="Fetching signatures..." />;
    const renderError = () => <Error errorText="Failed to retrieve signatures information." />;

    // small reusable row component
    const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
        <Grid item xs={12} sm={6} md={4}>
            <Box mb={1}>
                <Typography variant="body2" style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {label}
                </Typography>
                <Typography variant="body1">{value}</Typography>
            </Box>
        </Grid>
    );

    const renderAccordionSection = (title: string, content: JSX.Element): JSX.Element => {
        const panel = title.replace(/\s+/g, "").toLowerCase();
        return (
            <Box style={{ paddingBottom: "0px" }} className={dashboardPageStyles.workspace}>
                <Accordion className={formPageStyles.accordionCard} expanded={expanded === panel} onChange={() => toggleAccordion(panel)}>
                    <AccordionSummary
                        className={`${formPageStyles.accordionHeader} ${!isDesktop && formPageStyles.isMobileaccordionHeader} baseHeader`}
                        aria-controls="panel-content"
                        id={`panel-${panel}`}
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        {expanded === panel ? <RemoveIcon style={{ marginRight: 10 }} /> : <AddCircleIcon style={{ marginRight: 10 }} />}
                        <Typography className={documentsPageStyles.accordionTitle} variant="h5">
                            {title}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={formPageStyles.accordionDetails}>{content}</AccordionDetails>
                </Accordion>
            </Box>
        );
    };

    const renderFundingInformation = (): JSX.Element => {
        const fundingInformation = viewModel.model!.funding;
        const lenderContact = viewModel.lenderContact;
        const financialAdvisorContact = viewModel.financialAdvisorContact;

        if (!fundingInformation) {
            return (
                <Box flex="1 1 auto" overflow="auto" p={2}>
                    <Typography color="textSecondary">No funding information available.</Typography>
                </Box>
            );
        }

        const fundingInfo = [
            { label: "Is the purchaser using help to buy?", value: fundingInformation.isHelpToBuy ? "Yes" : "No" },
            { label: "Will you require a mortgage?", value: fundingInformation.mortgageRequired ? "Yes" : "No" },
        ];

        const lenderInfo = [
            { label: "Address line 1", value: lenderContact ? lenderContact.addressLine1 : fundingInformation.lenderAddress1 || "N/A" },
            { label: "Address line 2", value: lenderContact ? lenderContact.addressLine2 : fundingInformation.lenderAddress2 || "N/A" },
            { label: "Town/City", value: lenderContact ? lenderContact.city : fundingInformation.lenderCity || "N/A" },
            { label: "Postcode", value: lenderContact ? lenderContact.postcode : fundingInformation.lenderPostcode || "N/A" },
            { label: "Contact number", value: lenderContact ? lenderContact.phoneNumber : fundingInformation.lenderContactNumber || "N/A" },
            { label: "Fax number", value: lenderContact ? lenderContact.faxNumber : fundingInformation.lenderFaxNumber || "N/A" },
            { label: "Email", value: lenderContact ? lenderContact.email : fundingInformation.lenderEmail || "N/A" },
        ];

        const financialAdvisorInfo = [
            { label: "Address line 1", value: financialAdvisorContact ? financialAdvisorContact.addressLine1 : fundingInformation.financialAdvisorAddress1 || "N/A" },
            { label: "Address line 2", value: financialAdvisorContact ? financialAdvisorContact.addressLine2 : fundingInformation.financialAdvisorAddress2 || "N/A" },
            { label: "Town/City", value: financialAdvisorContact ? financialAdvisorContact.city : fundingInformation.financialAdvisorCity || "N/A" },
            { label: "Postcode", value: financialAdvisorContact ? financialAdvisorContact.postcode : fundingInformation.financialAdvisorPostcode || "N/A" },
            { label: "Contact number", value: financialAdvisorContact ? financialAdvisorContact.phoneNumber : fundingInformation.financialAdvisorContactNumber || "N/A" },
            { label: "Fax number", value: financialAdvisorContact ? financialAdvisorContact.faxNumber : fundingInformation.financialAdvisorFaxNumber || "N/A" },
            { label: "Email", value: financialAdvisorContact ? financialAdvisorContact.email : fundingInformation.financialAdvisorEmail || "N/A" },
        ];

        const lenderName = lenderContact ? lenderContact.name : fundingInformation.lenderName;
        const financialAdvisorName = financialAdvisorContact ? financialAdvisorContact.name : fundingInformation.financialAdvisorName;
        return (
            <Box flex="1 1 auto" overflow="auto" p={2}>
                <Grid className={formPageStyles.reservationFormBox} container spacing={2}>
                    {/* Funding Info */}
                    <Grid item xs={12}>
                        <Grid container>
                            {fundingInfo.map((row, idx) => (
                                <React.Fragment key={`funding-${idx}`}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography>
                                            <b>{row.label}</b>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography>{row.value}</Typography>
                                    </Grid>
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Lender Info */}
                    <Grid item xs={12}>
                        <Typography variant="h6">
                            <b> {lenderName} (Lender)</b>
                        </Typography>
                    </Grid>

                    {lenderInfo.map((row, idx) => (
                        <InfoRow key={`lender-${idx}`} label={row.label} value={row.value} />
                    ))}

                    {viewModel.lenderContacts && viewModel.lenderContacts.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    <b> Lender’s funding contacts</b>
                                </Typography>
                            </Grid>
                            <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                                {viewModel.lenderContacts.map((contact, index) => (
                                    <Grid item xs={12} key={`lender-funding-contact-${index}`}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                            <Box>
                                                <Typography variant="body1" style={{ fontWeight: "bold", color: "#333", marginBottom: "4px" }}>
                                                    {contact.firstName} {contact.lastName}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#666" }}>
                                                    {[contact.phoneNumber, contact.email].filter(Boolean).join(" - ")}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
                                                <Typography variant="body2" style={{ color: "#333" }}>
                                                    Email copy of:
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfReservationForm ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfReservationForm && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Reservation form
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfMemorandumofsale ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfMemorandumofsale && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Memorandum of sale
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Lender Info */}
                    <Grid item xs={12}>
                        <Typography variant="h6">
                            <b> {financialAdvisorName} (Financial Advisor)</b>
                        </Typography>
                    </Grid>

                    {financialAdvisorInfo.map((row, idx) => (
                        <InfoRow key={`financial-advisor-${idx}`} label={row.label} value={row.value} />
                    ))}

                    {viewModel.financialAdvisorContacts && viewModel.financialAdvisorContacts.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    <b> Financial advisor’s funding contacts</b>
                                </Typography>
                            </Grid>
                            <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                                {viewModel.financialAdvisorContacts.map((contact, index) => (
                                    <Grid item xs={12} key={`financial-advisor-funding-contact-${index}`}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                            <Box>
                                                <Typography variant="body1" style={{ fontWeight: "bold", color: "#333", marginBottom: "4px" }}>
                                                    {contact.firstName} {contact.lastName}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#666" }}>
                                                    {[contact.phoneNumber, contact.email].filter(Boolean).join(" - ")}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
                                                <Typography variant="body2" style={{ color: "#333" }}>
                                                    Email copy of:
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfReservationForm ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfReservationForm && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Reservation form
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfMemorandumofsale ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfMemorandumofsale && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Memorandum of sale
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        );
    };

    const renderSolicitorDetails = (): JSX.Element => {
        const solicitorInformation = viewModel.model!.solicitor;
        const purchaserContact = viewModel.purchaserContact;
        const sellerContact = viewModel.sellerContact;

        if (!solicitorInformation) {
            return (
                <Box flex="1 1 auto" overflow="auto" p={2}>
                    <Typography color="textSecondary">No solicitor information available.</Typography>
                </Box>
            );
        }

        const purchaserInfo = [
            { label: "Address line 1", value: purchaserContact ? purchaserContact.addressLine1 : solicitorInformation.purchaserSolicitorAddress1 },
            { label: "Address line 2", value: purchaserContact ? purchaserContact.addressLine2 : solicitorInformation.purchaserSolicitorAddress2 },
            { label: "Town/City", value: purchaserContact ? purchaserContact.city : solicitorInformation.purchaserSolicitorCity },
            { label: "Postcode", value: purchaserContact ? purchaserContact.postcode : solicitorInformation.purchaserSolicitorPostcode },
            { label: "Contact", value: purchaserContact ? purchaserContact.name : solicitorInformation.purchaserSolicitorName },
            { label: "Contact number", value: purchaserContact ? purchaserContact.phoneNumber : solicitorInformation.purchaserSolicitorContactNumber },
            { label: "Email", value: purchaserContact ? purchaserContact.email : solicitorInformation.purchaserSolicitorEmailAddress },
        ];

        const sellerInfo = [
            { label: "Address line 1", value: sellerContact ? sellerContact.addressLine1 : solicitorInformation.sellerSolicitorAddress1 },
            { label: "Address line 2", value: sellerContact ? sellerContact.addressLine2 : solicitorInformation.sellerSolicitorAddress2 },
            { label: "Town/City", value: sellerContact ? sellerContact.city : solicitorInformation.sellerSolicitorCity },
            { label: "Postcode", value: sellerContact ? sellerContact.postcode : solicitorInformation.sellerSolicitorPostcode },
            { label: "Contact", value: sellerContact ? sellerContact.name : solicitorInformation.sellerSolicitorName },
            { label: "Contact number", value: sellerContact ? sellerContact.phoneNumber : solicitorInformation.sellerSolicitorContactNumber },
            { label: "Email", value: sellerContact ? sellerContact.email : solicitorInformation.sellerSolicitorEmailAddress },
        ];

        const purchaserName = purchaserContact ? purchaserContact.name : solicitorInformation.purchaserSolicitorName;
        const sellerName = sellerContact ? sellerContact.name : solicitorInformation.sellerSolicitorName;

        return (
            <Box flex="1 1 auto" overflow="auto" p={2}>
                <Grid container spacing={2}>
                    {/* Purchaser Info */}
                    <Typography variant="h6">
                        <b> {purchaserName} (Purchaser’s solicitor)</b>
                    </Typography>

                    <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                        {purchaserInfo.map((row, idx) => (
                            <InfoRow key={`purchaser-${idx}`} label={row.label} value={row.value} />
                        ))}
                    </Grid>

                    {viewModel.purchaserSolicitorContacts && viewModel.purchaserSolicitorContacts.length > 0 && (
                        <>
                            <Typography variant="h6">
                                <b> Purchaser’s solicitor contacts</b>
                            </Typography>
                            <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                                {viewModel.purchaserSolicitorContacts.map((contact, index) => (
                                    <Grid item xs={12} key={`purchaser-solicitor-contact-${index}`}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                            <Box>
                                                <Typography variant="body1" style={{ fontWeight: "bold", color: "#333", marginBottom: "4px" }}>
                                                    {contact.firstName} {contact.lastName}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#666" }}>
                                                    {[contact.phoneNumber, contact.email].filter(Boolean).join(" - ")}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
                                                <Typography variant="body2" style={{ color: "#333" }}>
                                                    Email copy of:
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfReservationForm ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfReservationForm && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Reservation form
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfMemorandumofsale ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfMemorandumofsale && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Memorandum of sale
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} className={formPageStyles.dividerContainer}>
                        <Divider />
                    </Grid>

                    {/* Seller Info */}
                    <Typography variant="h6">
                        <b> {sellerName} (Seller’s solicitor)</b>
                    </Typography>

                    <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                        {sellerInfo.map((row, idx) => (
                            <InfoRow key={`seller-${idx}`} label={row.label} value={row.value} />
                        ))}
                    </Grid>

                    {viewModel.sellerSolicitorContacts && viewModel.sellerSolicitorContacts.length > 0 && (
                        <>
                            <Typography variant="h6">
                                <b> Seller’s solicitor contacts</b>
                            </Typography>
                            <Grid className={formPageStyles.reservationFormBox} container spacing={2} style={{ marginTop: "10px" }}>
                                {viewModel.sellerSolicitorContacts.map((contact, index) => (
                                    <Grid item xs={12} key={`seller-solicitor-contact-${index}`}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                            <Box>
                                                <Typography variant="body1" style={{ fontWeight: "bold", color: "#333", marginBottom: "4px" }}>
                                                    {contact.firstName} {contact.lastName}
                                                </Typography>
                                                <Typography variant="body2" style={{ color: "#666" }}>
                                                    {[contact.phoneNumber, contact.email].filter(Boolean).join(" - ")}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" style={{ gap: "16px" }}>
                                                <Typography variant="body2" style={{ color: "#333" }}>
                                                    Email copy of:
                                                </Typography>
                                                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfReservationForm ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfReservationForm && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Reservation form
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center">
                                                        <Box
                                                            width={16}
                                                            height={16}
                                                            border="1px solid #ccc"
                                                            borderRadius="2px"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            style={{
                                                                backgroundColor: contact.showCopyOfMemorandumofsale ? "#1976d2" : "white",
                                                                cursor: "default",
                                                                pointerEvents: "none",
                                                            }}
                                                        >
                                                            {contact.showCopyOfMemorandumofsale && (
                                                                <Typography variant="caption" style={{ color: "white", fontSize: "10px", marginLeft: "4px" }}>
                                                                    ✓
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" style={{ marginLeft: "8px", color: "#333" }}>
                                                            Memorandum of sale
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        );
    };

    const renderFinancialInfoAndDeclarations = (): JSX.Element => {
        if (viewModel.model === null || viewModel.model === undefined) {
            return <Typography>No financial information available</Typography>;
        }
        const propertyInfo = propertyViewModel.Property;
        const financialInformation = viewModel.model.financial;
        const financialAdditionalIncentives = viewModel.financialAdditionalIncentives;
        const earlyBirdInformation = viewModel.model.earlyBird;
        const reservationInformation = viewModel.model.reservation;

        const communicationMethods = [
            financialInformation && financialInformation.communicationPost && "Post",
            financialInformation && financialInformation.communicationEmail && "Email",
            financialInformation && financialInformation.communicationTelephone && "Telephone",
        ].filter(Boolean) as string[];

        const receivedCopyOfs = [
            financialInformation && financialInformation.readReservationAgreement && "Reservation agreement",
            financialInformation && financialInformation.readSummaryChecklist && "Summary Checklist attached at Appendix 1",
            financialInformation && financialInformation.confirmedNHQBCP && "New Homes Quality Board Code of Practice",
        ].filter(Boolean) as string[];

        const paymentMethods = [
            financialInformation && financialInformation.paymentCash && "Cash",
            financialInformation && financialInformation.paymentBankTransfer && "Bank transfer",
            financialInformation && financialInformation.paymentCheque && "Cheque",
            financialInformation && financialInformation.paymentCreditCard && "Credit card",
            financialInformation && financialInformation.paymentDebit && "Debit",
        ].filter(Boolean) as string[];

        const marketingMethods = [
            financialInformation && financialInformation.marketingFromSuppliers && "Our recommended suppliers",
            financialInformation && financialInformation.marketingFromUs && "Us",
        ].filter(Boolean) as string[];

        return (
            <Box flex="1 1 auto" overflow="auto" p={2}>
                <Grid container spacing={2}>
                    <Grid className={formPageStyles.reservationFormBox} item xs={12}>
                        <Typography>
                            <b>Agreed purchase price (£):</b> {financialInformation && financialInformation.agreedPrice ? financialInformation.agreedPrice : "N/A"}
                        </Typography>
                        <Typography>
                            <b>Agreed purchase price in words:</b> {financialInformation && financialInformation.agreedPriceWords ? financialInformation.agreedPriceWords : "N/A"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} className={formPageStyles.dividerContainer}>
                        <Divider />
                    </Grid>

                    <Grid className={formPageStyles.reservationFormBox} item xs={12}>
                        {financialInformation && financialInformation.showDeposit && (
                            <>
                                {!financialInformation.amountDeposit && financialInformation.percentageDeposit ? (
                                    <Typography>
                                        <span style={{ fontWeight: "bold" }}>Percentage deposit:</span>
                                        <span>{financialInformation && financialInformation.amount ? <span>{financialInformation.percentageDeposit}%</span> : "N/A"}</span>
                                    </Typography>
                                ) : (
                                    <Typography>
                                        <span style={{ fontWeight: "bold" }}>Exact deposit amount:</span>
                                        <span>
                                            {financialInformation && financialInformation.amountDeposit ? (
                                                <span>
                                                    £ {""}
                                                    {financialInformation.amountDeposit}
                                                </span>
                                            ) : (
                                                "N/A"
                                            )}
                                        </span>
                                    </Typography>
                                )}

                                {propertyInfo && propertyInfo.schemeType && (propertyInfo.schemeType == 3 || propertyInfo.schemeType == 4) && (
                                    <Typography>
                                        <span style={{ fontWeight: "bold" }}>Percentage owned:</span>
                                        <span>{financialInformation && financialInformation.percentageOwned ? <span>{financialInformation.percentageOwned}%</span> : "N/A"}</span>
                                    </Typography>
                                )}

                                <Typography>
                                    <span style={{ fontWeight: "bold" }}>Deposit amount:</span>
                                    <span>
                                        {financialInformation && financialInformation.amount ? (
                                            <>
                                                £ {""}
                                                <span>{currencyFormat(financialInformation.amount)}</span>
                                            </>
                                        ) : (
                                            "N/A"
                                        )}
                                    </span>
                                </Typography>
                            </>
                        )}

                        <Typography>
                            <span style={{ fontWeight: "bold" }}>Early bird reservation fee paid:</span>
                            <span>
                                {earlyBirdInformation && earlyBirdInformation.reservationFeePaid ? (
                                    <span>
                                        £ {""}
                                        <span>{currencyFormat(earlyBirdInformation.reservationFeePaid)}</span>
                                    </span>
                                ) : (
                                    "N/A"
                                )}
                            </span>
                        </Typography>

                        <Typography>
                            <span style={{ fontWeight: "bold" }}>Reservation fee to be paid:</span>
                            <span>
                                {reservationInformation && reservationInformation.reservationFeePaid ? (
                                    <span>
                                        £ {""}
                                        <span
                                            style={{
                                                color: parseFloat(reservationInformation.reservationFeePaid) < 0 ? "red" : "inherit",
                                            }}
                                        >
                                            {currencyFormat(reservationInformation.reservationFeePaid)}
                                        </span>
                                    </span>
                                ) : (
                                    "N/A"
                                )}
                            </span>
                        </Typography>

                        <Typography>
                            <span>
                                Purchaser balance deposit due at {""}
                                <span style={{ fontWeight: "bold" }}>{financialInformation && financialInformation.depositDueAt ? financialInformation.depositDueAt : "N/A"}</span>:
                            </span>
                            <span style={{ fontWeight: "bold" }}>
                                {viewModel && viewModel.balanceDepositDue ? (
                                    <span>
                                        £ {""}
                                        <span
                                            style={{
                                                color: viewModel.balanceDepositDue < 0 ? "red" : "inherit",
                                            }}
                                        >
                                            {currencyFormat(viewModel.balanceDepositDue.toFixed(2))}
                                        </span>
                                    </span>
                                ) : (
                                    "N/A"
                                )}
                            </span>
                        </Typography>

                        {financialInformation && financialInformation.incentive && viewModel.financialIncentive && (
                            <Typography>
                                <span>
                                    Incentive - {""}
                                    <span style={{ fontWeight: "bold" }}>{viewModel.financialIncentive}</span>:
                                </span>
                                <span style={{ fontWeight: "bold" }}>
                                    {financialInformation.amount ? (
                                        <span>
                                            £ {""}
                                            <span>{currencyFormat(financialInformation.amount)}</span>
                                        </span>
                                    ) : (
                                        "N/A"
                                    )}
                                </span>
                            </Typography>
                        )}

                        {financialAdditionalIncentives &&
                            financialAdditionalIncentives.length > 0 &&
                            financialAdditionalIncentives.map((incentive, index) => (
                                <Typography key={`incentive-${index}`}>
                                    <span>
                                        Additional Incentive - {""}
                                        <span style={{ fontWeight: "bold" }}>{incentive.displayName}</span>:
                                    </span>
                                    <span style={{ fontWeight: "bold" }}>
                                        {incentive.amount ? (
                                            <span>
                                                £ {""}
                                                <span>{currencyFormat(incentive.amount.toString())}</span>
                                            </span>
                                        ) : (
                                            "N/A"
                                        )}
                                    </span>
                                </Typography>
                            ))}
                    </Grid>

                    <Grid item xs={12} className={formPageStyles.dividerContainer}>
                        <Divider />
                    </Grid>

                    {/* Special conditions */}
                    <Grid className={formPageStyles.reservationDetailsBox} item xs={12}>
                        <Typography>
                            <b>Special conditions</b>
                        </Typography>
                        <Typography
                            component="div"
                            dangerouslySetInnerHTML={{
                                __html: financialInformation && financialInformation.specialConditions ? financialInformation.specialConditions : "",
                            }}
                        />
                    </Grid>

                    {/* Grey panel with legal notice, deposit, and data protection */}
                    <Grid className={formPageStyles.reservationDetailsBox} item xs={12}>
                        <Box bgcolor={theme.palette.grey[200]} p={3} borderRadius={4}>
                            <Typography variant="subtitle1" style={{ fontWeight: 700 }} paragraph>
                                This is a legal agreement; if in any doubt as to its meaning or effect, the buyer should seek legal advice before signing.
                            </Typography>

                            <Box className={formPageStyles.reservationInnerBox} mb={2}>
                                <Typography>
                                    <span>
                                        I/we the purchaser has/have paid a deposit of{" "}
                                        <span style={{ fontWeight: "bold" }}>£{isNullOrUndefined(viewModel.depositValue) ? "0" : viewModel.depositValue}</span>:
                                    </span>
                                    <span style={{ fontWeight: "bold" }}>{paymentMethods.length > 0 ? formatList(paymentMethods) : "N/A"}</span>
                                </Typography>
                            </Box>

                            <Typography variant="subtitle1" style={{ fontWeight: 700 }} paragraph>
                                Data protection act. Notice to purchaser.
                            </Typography>
                            <Typography variant="body1" paragraph>
                                We will aim to keep your personal information up to date, but rely on you telling us promptly if your name, address, telephone number or any other
                                details change. We will not pass your personal information to anyone outside our group, except to third parties for the purpose of providing
                                products, services and/or marketing information to you (e.g. professional advisors, finance providers, estate agents, recommended suppliers). We may
                                also copy this form to relevant third parties.
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Your personal information may be used by us and any of the persons mentioned above for: internal purposes and to provide products, services and/or
                                marketing information to you.
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Your personal information may also be used for research purposes and to help us develop our website, products and/or services.
                            </Typography>
                            <Box className={formPageStyles.reservationInnerBox}>
                                <Typography variant="body1" paragraph>
                                    Your preferred method of communication: <b>{communicationMethods.length > 0 ? formatList(communicationMethods) : "N/A"}</b>
                                </Typography>
                            </Box>
                            <Typography variant="body1" paragraph>
                                You wish to receive marketing information on properties, research, products and/or services that might be of interest to you from{" "}
                                <b>{marketingMethods.length > 0 ? formatList(marketingMethods) : "N/A"}</b> only
                            </Typography>

                            <Box className={formPageStyles.reservationInnerBox}>
                                <Typography variant="body1" paragraph>
                                    We can confirm you have read/confirm/received a copy of: <b>{receivedCopyOfs.length > 0 ? formatList(receivedCopyOfs) : "N/A"}</b>
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderReservationForm = (): JSX.Element => {
        const reservationInformation = viewModel.model!.reservation;
        if (!reservationInformation) {
            return (
                <Box flex="1 1 auto" overflow="auto" p={2}>
                    <Typography color="textSecondary">No reservation information available.</Typography>
                </Box>
            );
        }

        return (
            <Box flex="1 1 auto" overflow="auto" p={2}>
                <Box className={formPageStyles.reservationFormBox} mb={2}>
                    <Typography>
                        <b>Sales agent:</b> {reservationInformation.salesAgentName}
                    </Typography>
                    <Typography>
                        <b>Reservation type:</b> {reservationInformation.reservationType}
                    </Typography>
                    <Typography>
                        <b>Agreed purchase price (£):</b> {reservationInformation.agreedPurchasePrice}
                    </Typography>
                    <Typography>
                        <b>Anticipated completion date:</b> {reservationInformation.anticipatedCompletedDateFull}
                    </Typography>
                    <Typography>
                        <b>Reservation fee paid (£):</b> {reservationInformation.reservationFeePaid}
                    </Typography>
                    <Typography>
                        <b>Exchange by date:</b> {reservationInformation.exchangeDateFormatted}
                    </Typography>
                    <Typography>
                        <b>Reservation expiry date:</b> {reservationInformation.expiryDateFormatted}
                    </Typography>
                </Box>

                <Divider />

                <Box className={formPageStyles.reservationFormBox} mt={2} mb={2}>
                    <Typography>
                        <b>Tenure:</b> {reservationInformation.isFreehold ? "Freehold" : "Leasehold"}
                    </Typography>
                    <Typography>
                        <b>Lease commencing on:</b> {reservationInformation.leaseStartDateFormatted}
                    </Typography>
                    <Typography>
                        <b>Lease terms (years):</b> {reservationInformation.leaseYears}
                    </Typography>
                    <Typography>
                        <b>Initial ground rent:</b> {reservationInformation.initialGroundRent}
                    </Typography>
                    <Typography>
                        <b>Management service fees:</b> {reservationInformation.managementServiceFees}
                    </Typography>
                </Box>

                <Divider />

                <Box className={formPageStyles.reservationFormBox} mt={2}>
                    <Typography>
                        <b>Purchaser Position:</b> {viewModel.model && viewModel.model.purchaser ? viewModel.model.purchaser.purchaserPosition : "N/A"}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderSignatureSection = (): JSX.Element => {
        return (
            <Box flex="0 0 auto" p={2} bgcolor="background.paper">
                <Typography variant="subtitle1" gutterBottom>
                    {viewModel && viewModel.isPurchaserOneLoggedIn ? "Purchaser 1 signature" : "Purchaser 2 signature"}
                </Typography>

                <Box mb={2}>
                    <Box className={formPageStyles.signatureCanvas}>
                        <SignatureCanvas
                            ref={c => {
                                // keep ref stable
                                signatureCanvasRef.current = c;
                            }}
                            penColor="black"
                            canvasProps={{
                                width: 540,
                                height: 90,
                                className: "sigCanvas",
                                style: { width: "100%", height: "90px" },
                            }}
                            onEnd={handleSignatureEnd}
                        />
                    </Box>
                    <Box className={formPageStyles.signatureControls}>
                        <Box className={formPageStyles.signatureDateField}>
                            <EditableInput
                                label="Date"
                                type="date"
                                fieldName={viewModel && viewModel.isPurchaserOneLoggedIn ? "financial.purchaserOneSignatureDate" : "financial.purchaserTwoSignatureDate"}
                                viewModel={viewModel as any}
                            />
                            <Link color="secondary" onClick={handleSignatureClear} className={formPageStyles.signatureClearButton}>
                                Clear
                            </Link>
                        </Box>
                        <Button
                            variant="contained"
                            className={formPageStyles.signatureSubmitButton}
                            style={{ backgroundColor: "#556ED1", color: "#fff", fontSize: "12px" }}
                            disabled={isSubmittingSignature}
                            onClick={() => {
                                const dataUrl = signatureCanvasRef.current ? signatureCanvasRef.current.toDataURL() : "";
                                if (dataUrl && !isSubmittingSignature) {
                                    setIsSubmittingSignature(true);
                                    viewModel
                                        .submitPurchaserSignature(dataUrl)
                                        .then(() => {
                                            setShowSignatureSuccess(true);
                                        })
                                        .catch(error => {
                                            console.error("Failed to submit signature:", error);
                                        })
                                        .finally(() => {
                                            setIsSubmittingSignature(false);
                                        });
                                }
                            }}
                        >
                            {isSubmittingSignature ? "Submitting..." : "Submit signature"}
                        </Button>
                    </Box>
                </Box>

                <Box mt={2}>
                    <Typography variant="body2">
                        Have a query about something in the form? Contact your sales representative at <a href="tel:01743123456">01743 123456</a>
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderFormHeader = (): JSX.Element => (
        <Box className={formPageStyles.formHeader}>
            <Typography className="title" component="h2">
                Full reservation
            </Typography>
        </Box>
    );

    const renderLegalModal = (): JSX.Element => {
        return (
            <Dialog
                open={showLegalModal}
                fullWidth
                maxWidth="md"
                aria-labelledby="legal-info-title"
                PaperProps={{
                    style: {
                        zIndex: 2000,
                        maxHeight: "90vh",
                        display: "flex",
                        flexDirection: "column",
                    },
                }}
            >
                <DialogTitle id="legal-info-title">
                    <b>Legal information</b>
                </DialogTitle>
                <DialogContent
                    dividers
                    style={{
                        flex: 1,
                        overflow: "hidden",
                        padding: 0,
                    }}
                >
                    <Box
                        // @ts-ignore MUI Box ref typing
                        ref={legalScrollCombinedRef}
                        onScroll={handleLegalScroll}
                        style={{
                            height: "100%",
                            maxHeight: isDesktop ? "65vh" : "60vh",
                            overflow: "auto",
                            padding: "16px",
                        }}
                    >
                        <Typography
                            component="div"
                            dangerouslySetInnerHTML={{
                                __html: viewModel && viewModel.legalCopy ? viewModel.legalCopy : "",
                            }}
                        />
                    </Box>
                </DialogContent>
                {legalScrolledToBottom && (
                    <DialogActions style={{ flexShrink: 0 }}>
                        <Button variant="contained" onClick={closeLegalModal} fullWidth style={{ backgroundColor: "#556ED1", color: "#fff" }}>
                            Ok
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        );
    };

    const renderItems = () => {
        const signatureFooterHeight = 55;
        return (
            <Box
                className={`${formPageStyles.root} ${documentsPageStyles.documentPage} ${!isDesktop && documentsPageStyles.ismobileViewAccordionBlock} baseViewAccordionBlock`}
                style={{ minHeight: 0 }}
            >
                <Box
                    // @ts-ignore MUI Box ref typing
                    ref={scrollContainerRef}
                    onScroll={handleContentScroll}
                    className={formPageStyles.formContent}
                >
                    {showScrollHint && (
                        <Box
                            position="fixed"
                            left="50%"
                            bottom={signatureFooterHeight + 30}
                            display="flex"
                            alignItems="center"
                            bgcolor="#556ED1"
                            color="#fff"
                            px={isDesktop ? 3 : 2}
                            py={isDesktop ? 2 : 1.5}
                            borderRadius={25}
                            zIndex={1400}
                            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                            style={{
                                transform: "translateX(-50%)",
                                left: "50%",
                                cursor: "pointer",
                                maxWidth: isDesktop ? "none" : "90%",
                            }}
                            onClick={handleHintClick}
                        >
                            <Typography variant="body2" style={{ fontWeight: 500, fontSize: isDesktop ? "14px" : "12px" }}>
                                Scroll the form
                            </Typography>
                        </Box>
                    )}
                    {renderReservationForm()}
                    {renderAccordionSection("Funding Information", renderFundingInformation())}
                    {renderAccordionSection("Solicitor Details", renderSolicitorDetails())}
                    {renderAccordionSection("Financial Information & Declarations", renderFinancialInfoAndDeclarations())}
                </Box>

                {/* Fixed signature section */}
                <Box
                    className={formPageStyles.signatureBottomSection}
                    // @ts-ignore MUI Box ref typing
                    ref={footerRef}
                    flexShrink={0}
                    position="fixed"
                    left={isDesktop ? 80 : 0}
                    right={0}
                    bottom={0}
                    bgcolor="background.paper"
                    borderTop={`1px solid ${theme.palette.divider}`}
                    zIndex={1300}
                    p={2}
                >
                    {renderSignatureSection()}
                </Box>
            </Box>
        );
    };

    const renderFormSection = (): JSX.Element => {
        return (
            <Box className={formPageStyles.root} display="flex" flexDirection="column" height="100%" style={{ minHeight: 0 }}>
                <Grid container spacing={0}>
                    <Grid item xs={12} sm={12}>
                        {renderFormHeader()}
                        {renderItems()}
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderBackToSignatures = () =>
        isDesktop ? (
            <Box className={formPageStyles.backToSignatures}>
                <SquareLeftArrow className="arrowIcon" onClick={() => viewModel.navigateToSignatures(propertyId)} style={{ cursor: "pointer" }} />
                <NavButton displayName="Back to signature section" />
            </Box>
        ) : null;

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${formPageStyles.pageWrapper}`}>
            <Box className={`${dashboardPageStyles.workspace} ${mainPageStyles.workspace}`}>
                {viewModel.ApiStatus === ApiStatus.success && (
                    <React.Fragment>
                        {!showAlreadySignedModal && renderLegalModal()}
                        {renderAlreadySignedModal()}
                        {renderSignatureSuccessModal()}
                        {renderBackToSignatures()}
                        {!showLegalModal && !showAlreadySignedModal && renderFormSection()}
                    </React.Fragment>
                )}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>
        </Box>
    ));
};
